import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  rangeControl,
  toggleControl,
  stat,
  statRow,
  button,
} from "../ui/components";
import { plot, PALETTE } from "../ui/charts";
import { heatGrid, mixHex } from "../ui/grid";
import { themeColors, h2, legend, caption, ticker } from "./shared";
import { CountMinSketch } from "../core/count-min-sketch";
import { formatBytes, formatCount, formatNumber } from "../ui/format";
import type { PageRenderer } from "../ui/router";

const VOCAB = 200;
const ZIPF_S = 1.1;

export const renderCountMin: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Frequency · heavy hitters",
      title: "Count-Min Sketch",
      lede: "Estimate how often each item appears in a stream, in a small fixed grid of counters. It never undercounts — its only error is a bounded overcount — which makes it perfect for finding the heavy hitters.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "The sketch is a grid of ",
        el("em", {}, "d"),
        " rows × ",
        el("em", {}, "w"),
        " columns. Each row has its own hash function; to record an item you add 1 to one counter per row. To estimate its count you read those same ",
        el("em", {}, "d"),
        " counters and take the ",
        el("strong", {}, "minimum"),
        ". Collisions can only push counters up, so the minimum is never below the truth — and the overcount is bounded by ",
        el("strong", {}, "ε·N"),
        " with probability 1 − δ, where w ≈ e/ε and d ≈ ln(1/δ).",
      ),
    ),
  );

  playgroundCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { CountMinSketch } from "./core";

// Overcount ≤ 0.1% of the stream size, 99% of the time.
const cms = CountMinSketch.create(0.001, 0.01);
for (const path of requestLog) cms.add(path);

cms.estimate("/api/login"); // ≥ true count, over by at most ε·N
cms.totalCount;             // N`,
    ),
  );

  append(
    outlet,
    callout(
      "tip",
      el(
        "div",
        {},
        el("strong", {}, "Conservative update. "),
        "Toggle it on above: when recording an item, only the counters currently holding the minimum get raised. You keep the never-undercount guarantee but shrink the overcount noticeably — especially for rare items drowned out by heavy hitters.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------

function makeZipf(): () => string {
  const weights: number[] = [];
  let total = 0;
  for (let r = 1; r <= VOCAB; r++) {
    const w = 1 / Math.pow(r, ZIPF_S);
    weights.push(w);
    total += w;
  }
  const cum: number[] = [];
  let acc = 0;
  for (const w of weights) {
    acc += w / total;
    cum.push(acc);
  }
  return () => {
    const x = Math.random();
    let lo = 0;
    let hi = cum.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cum[mid] < x) lo = mid + 1;
      else hi = mid;
    }
    return `term-${lo + 1}`;
  };
}

function playgroundCard(outlet: HTMLElement): void {
  const wExp = signal(6); // width = 2^wExp
  const depthSig = signal(4);
  const conservative = signal(false);
  const query = signal("term-1");
  const { tick, bump } = ticker();

  const sample = makeZipf();
  const truth = new Map<string, number>();
  let cms = build();
  let grid = heatGrid(1, 1);
  const gridHost = el("div", { class: "viz-wrap" });

  function build(): CountMinSketch {
    return new CountMinSketch({ width: 2 ** wExp.value, depth: depthSig.value, conservative: conservative.value, seed: 7 });
  }
  function rebuildGrid(): void {
    const w = cms.width;
    const cell = Math.min(16, Math.max(3, Math.floor(560 / w)));
    grid = heatGrid(w, cms.depth, { cellSize: cell, gap: cell >= 6 ? 2 : 1 });
    gridHost.replaceChildren(grid.canvas);
  }

  // Rebuild on any structural change; clears the stream (can't be replayed cheaply).
  effect(() => {
    wExp.value;
    depthSig.value;
    conservative.value;
    cms = build();
    truth.clear();
    rebuildGrid();
    bump();
  });

  function feed(count: number): void {
    for (let i = 0; i < count; i++) {
      const t = sample();
      cms.add(t);
      truth.set(t, (truth.get(t) ?? 0) + 1);
    }
    bump();
  }

  const chartHost = el("div", { class: "viz-wrap" });

  effect(() => {
    tick.value;
    const c = themeColors();
    let max = 1;
    for (let i = 0; i < cms.table.length; i++) max = Math.max(max, cms.table[i]);
    const q = query.value.trim();
    const cells = q ? new Set(cms.cells(q).map((cell) => cell.row * cms.width + cell.col)) : new Set<number>();
    grid.draw((i) => {
      if (cells.has(i)) return c.accent2;
      const v = cms.table[i];
      if (v === 0) return c.bg2;
      return mixHex(c.border, c.warn, Math.min(1, v / max));
    });

    // Estimated vs actual scatter for all distinct items.
    const pts: Array<[number, number]> = [];
    let maxF = 10;
    for (const [k, t] of truth) {
      pts.push([t, cms.estimate(k)]);
      maxF = Math.max(maxF, t, cms.estimate(k));
    }
    chartHost.replaceChildren(
      plot({
        height: 300,
        xLabel: "true frequency",
        yLabel: "estimated frequency",
        xFormat: formatCount,
        yFormat: formatCount,
        series: [
          { type: "line", points: [[0, 0], [maxF, maxF]], color: c.textFaint, dashed: true, label: "exact" },
          { type: "scatter", points: pts, color: PALETTE[3], radius: 3, label: "estimate ≥ truth" },
        ],
        legend: true,
      }),
    );
  });

  const queryInfo = el("div", { class: "mini-note", style: { minHeight: "1.3em" } });
  effect(() => {
    tick.value;
    const q = query.value.trim();
    if (!q) {
      queryInfo.textContent = "";
      return;
    }
    const t = truth.get(q) ?? 0;
    const est = cms.estimate(q);
    queryInfo.textContent = `“${q}”: true ${formatNumber(t)}, estimated ${formatNumber(est)} (over by ${formatNumber(est - t)})`;
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    el(
      "div",
      { class: "button-row" },
      button({ label: "+10K events", variant: "primary", onClick: () => feed(10000) }),
      button({ label: "+50K events", variant: "ghost", onClick: () => feed(50000) }),
    ),
    rangeControl({ label: "width w", min: 4, max: 9, signal: wExp, format: (e) => `${2 ** e}` }),
    rangeControl({ label: "depth d (rows)", min: 2, max: 6, signal: depthSig, format: (d) => `${d}` }),
    toggleControl({ label: "conservative update", signal: conservative, hint: "raise only the minimum counters" }),
    el(
      "label",
      { class: "control" },
      el("span", { class: "control-label" }, "Query an item"),
      el("input", {
        class: "textinput",
        type: "text",
        value: "term-1",
        placeholder: "term-1 … term-200",
        oninput: (e: Event) => (query.value = (e.target as HTMLInputElement).value),
      }),
    ),
    queryInfo,
    statRow(
      stat({ label: "Events (N)", value: () => (tick.value, formatCount(cms.totalCount)) }),
      stat({ label: "Distinct", value: () => (tick.value, formatCount(truth.size)) }),
    ),
    statRow(
      stat({ label: "Error bound ε·N", accent: true, value: () => (tick.value, formatNumber(cms.errorBound())) }),
      stat({ label: "Memory", value: () => (tick.value, formatBytes(cms.memoryBytes())) }),
    ),
  );

  const viz = el(
    "div",
    {},
    gridHost,
    legend([
      [themeColors().warn, "counter (hotter = higher)"],
      [themeColors().accent2, "queried item's cells"],
    ]),
    caption("Feed a skewed (Zipf) stream. Every point sits on or above the diagonal — the sketch never undercounts. Heavy hitters (top-right) stay tight."),
    chartHost,
  );

  append(
    outlet,
    card({
      title: "Estimate frequencies on a skewed stream",
      subtitle: "A handful of terms dominate, just like real traffic. Watch the estimates stay above the truth, bounded by ε·N.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}
